import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { FileText, CheckCircle, Clock, Printer, Download } from 'lucide-react';

const DIMENSION_COLORS = {
  conscientiousness: '#0071e3', openness: '#34C759', stability: '#FF9500',
  agreeableness: '#AF52DE', extraversion: '#FF2D55',
};

const FinalDoc = () => {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_BASE}/candidate-report/final/${user.id}`)
      .then(res => { if (res.data.has_final) setReport(res.data.report); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user.id]);

  if (loading) return <div className="p-12 flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0071e3]" /></div>;

  if (!report) {
    return (
      <div className="p-4 sm:p-8 md:p-12">
        <div className="max-w-3xl mx-auto text-center">
          <Clock className="w-12 h-12 text-[#86868b] mx-auto mb-4" />
          <h1 className="text-3xl font-semibold text-[#1d1d1f] mb-3">Final Documentation</h1>
          <p className="text-lg text-[#86868b]">Your final document will appear here once your career counsellor has completed their review and recommendation.</p>
        </div>
      </div>
    );
  }

  const rd = report.report_data || {};
  const resume = rd.resume || {};
  const p = resume.personal || {};
  const e = resume.education || {};
  const w = resume.work || {};
  const sk = resume.skills || {};
  const personality = rd.personality || {};
  const pType = personality.type || {};
  const pScores = personality.scores || {};
  const aptitude = rd.aptitude_scores || {};
  const dimOrder = ['conscientiousness', 'openness', 'stability', 'agreeableness', 'extraversion'];

  return (
    <div className="p-4 sm:p-8 md:p-12">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6 print:hidden">
          <div>
            <h1 className="text-3xl font-semibold text-[#1d1d1f]">Final Documentation</h1>
            <p className="text-base text-[#86868b]">Your complete candidate package — ready for employers</p>
          </div>
          <Button onClick={() => window.print()} className="bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-full">
            <Printer className="w-4 h-4 mr-2" /> Print Document
          </Button>
        </div>

        <div className="bg-green-50 rounded-2xl border border-green-200 p-4 mb-6 flex items-center gap-3 print:hidden">
          <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-green-800">Document Complete</p>
            <p className="text-xs text-green-700">Reviewed by your career counsellor on {new Date(report.reviewed_at).toLocaleDateString('en-IE')}</p>
          </div>
        </div>

        {/* The Document */}
        <div className="bg-white rounded-2xl border border-[#d2d2d7] print:rounded-none print:border-0 overflow-hidden" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
          {/* Page 1: CV */}
          <div className="p-8 sm:p-10 print:p-8">
            <div className="text-center mb-6 pb-4 border-b border-gray-200">
              <p className="text-xs text-[#86868b] uppercase tracking-wider mb-1">ApprenticeshipMatch.ie</p>
              <h1 className="text-xl font-bold text-black">Candidate Assessment Report</h1>
              <p className="text-xs text-[#86868b] mt-1">Ref: {rd.candidate_ref}</p>
            </div>

            <h2 className="text-lg font-bold text-black uppercase">{p.firstName} {p.lastName}</h2>
            <p className="text-xs text-gray-600 mb-4">
              {[p.county, p.phone, p.email, p.dob ? `DOB: ${p.dob}` : null].filter(Boolean).join('  |  ')}
            </p>

            {/* Personal Statement */}
            {(() => {
              const val = resume.values || {};
              const handWritten = val.personalStatement?.trim();
              const autoStmt = (val.environment || val.goals?.length > 0) ? [
                val.environment ? `I am seeking an apprenticeship in a ${val.environment.toLowerCase()} environment.` : '',
                val.goals?.length > 0 ? `My goals include ${val.goals.map(g => g.toLowerCase()).join(', ')}.` : '',
                val.topValues?.length > 0 ? `I value ${val.topValues.map(v => v.toLowerCase()).join(', ')} in my work.` : '',
                'I am highly motivated, reliable and committed to completing a structured apprenticeship programme that combines hands-on experience with formal qualification.',
              ].filter(Boolean).join(' ') : null;
              const stmt = handWritten || autoStmt;
              return stmt ? (
                <FinalSection title="Personal Statement">
                  <p className="text-xs text-gray-700 leading-relaxed">{stmt}</p>
                </FinalSection>
              ) : null;
            })()}

            {e.school && (
              <FinalSection title="Education">
                <p className="text-xs font-semibold text-black">{e.school}</p>
                {e.level && <p className="text-xs text-gray-700">{e.level}</p>}
                {(e.lcResults || []).length > 0 && (
                  <ul className="ml-4 mt-1">{e.lcResults.map(r => <li key={r.subject} className="text-xs text-gray-700 list-disc">{r.subject}{r.grade ? ` — ${r.grade}` : ''}</li>)}</ul>
                )}
              </FinalSection>
            )}

            {w.hasWork === 'Yes' && (
              <FinalSection title="Work Experience">
                {[0,1,2,3].map(i => { const role = w[`role${i}`]; if (!role?.title) return null; return (
                  <div key={i} className={i > 0 ? 'mt-3' : ''}>
                    <p className="text-xs font-semibold text-black">{role.employer}</p>
                    <p className="text-xs text-gray-700 italic">{role.title}{role.duration ? ` (${role.duration})` : ''}</p>
                    {(role.tasks||[]).length > 0 && <ul className="ml-4 mt-0.5">{role.tasks.map(t => <li key={t} className="text-xs text-gray-700 list-disc">{t}</li>)}</ul>}
                  </div>
                ); })}
                {w.hasVolunteer === 'Yes' && w.volunteerDesc && (
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-black">Volunteering</p>
                    <p className="text-xs text-gray-700">{w.volunteerDesc}</p>
                  </div>
                )}
              </FinalSection>
            )}

            {(() => {
              const allSkills = [...(sk.digital||[]), ...(sk.practical||[]), ...(sk.soft||[])];
              if (p.licence) allSkills.push(`${p.licence} Driving Licence`);
              if (p.transport === 'Yes') allSkills.push('Own transport available');
              return allSkills.length > 0 ? (
                <FinalSection title="Skills & Certifications">
                  <ul className="ml-4">{allSkills.map((s,i) => <li key={i} className="text-xs text-gray-700 list-disc">{s}</li>)}</ul>
                </FinalSection>
              ) : null;
            })()}

            {(() => {
              const int = resume.interests || {};
              const ach = resume.achievements || {};
              return ((int.list||[]).length > 0 || (ach.achievements||[]).length > 0) ? (
                <FinalSection title="Interests & Activities">
                  {(int.list||[]).length > 0 && <ul className="ml-4">{int.list.map(item => <li key={item} className="text-xs text-gray-700 list-disc"><span className="font-semibold text-black">{item}</span>{int.level ? ` — ${int.level} level` : ''}</li>)}</ul>}
                  {(ach.achievements||[]).length > 0 && <ul className="mt-1 ml-4">{ach.achievements.map(a => <li key={a} className="text-xs text-gray-700 list-disc">{a}</li>)}</ul>}
                  {(ach.leadership||[]).length > 0 && <ul className="mt-1 ml-4">{ach.leadership.map(l => <li key={l} className="text-xs text-gray-700 list-disc"><span className="font-semibold text-black">Leadership:</span> {l}</li>)}</ul>}
                </FinalSection>
              ) : null;
            })()}

            <FinalSection title="References">
              <p className="text-xs text-gray-700">Available on request.</p>
            </FinalSection>
          </div>

          <div className="border-t-2 border-dashed border-gray-200 print:break-before-page" />

          {/* Page 2: Assessment */}
          <div className="p-8 sm:p-10 print:p-8">
            <FinalSection title="Personality Profile">
              {pType.type_name && <><p className="text-xs text-[#86868b]">Type</p><h3 className="text-base font-bold text-black">{pType.type_name}</h3><p className="text-xs text-[#86868b] mb-2">{pType.keywords}</p></>}
              {pType.description && <p className="text-xs text-gray-700 leading-relaxed mb-3">{pType.description}</p>}
              <div className="space-y-2">
                {dimOrder.map(dim => { const data = pScores[dim]; if (!data) return null; return (
                  <div key={dim} className="flex items-center gap-3">
                    <span className="text-xs text-gray-700 w-40">{data.label}</span>
                    <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${data.score*10}%`, backgroundColor: DIMENSION_COLORS[dim] }} /></div>
                    <span className="text-xs font-bold w-10 text-right">{data.score}/10</span>
                  </div>
                ); })}
              </div>
            </FinalSection>

            <FinalSection title="Aptitude Test Results">
              <div className="space-y-3">
                {Object.entries(aptitude).map(([tid, data]) => (
                  <div key={tid} className="flex items-center gap-3">
                    <span className="text-xs text-gray-700 w-40">{data.title}</span>
                    <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full rounded-full bg-[#0071e3]" style={{ width: `${data.score}%` }} /></div>
                    <span className="text-xs font-bold w-10 text-right">{data.score}%</span>
                    {data.band && <span className="text-xs font-semibold w-24 text-right" style={{ color: data.band === 'Exceptional' ? '#34C759' : data.band === 'Strong' ? '#0071e3' : '#FF9500' }}>{data.band}</span>}
                  </div>
                ))}
              </div>
            </FinalSection>

            {(rd.sectors_of_interest?.length > 0 || rd.location_preferences?.county) && (
              <FinalSection title="Candidate Preferences">
                {rd.sectors_of_interest?.length > 0 && <p className="text-xs text-gray-700"><span className="font-semibold">Sectors:</span> {rd.sectors_of_interest.join(', ')}</p>}
                {rd.location_preferences?.county && <p className="text-xs text-gray-700"><span className="font-semibold">Location:</span> {rd.location_preferences.county}</p>}
              </FinalSection>
            )}

            <FinalSection title="Career Counsellor Recommendation">
              <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">{report.recommendation}</p>
              </div>
            </FinalSection>
          </div>
        </div>
      </div>
    </div>
  );
};

const FinalSection = ({ title, children }) => (
  <div style={{ marginTop: '36px' }}>
    <h3 className="text-xs font-bold text-black uppercase tracking-wider mb-2" style={{ letterSpacing: '0.8px' }}>{title}</h3>
    <div className="border-t border-gray-300 pt-3">{children}</div>
  </div>
);

export default FinalDoc;
